import { useEffect, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ScanSearch, ArrowRight, ArrowLeft, Info, Plus, X, ChevronDown, ChevronUp, CheckCircle2, Loader2,
  Globe, Ban, HelpCircle, Building2, MapPin, Pencil, ShieldQuestion, Sparkles, ExternalLink, Radar,
  Bookmark, BookmarkCheck, RefreshCw, Clock, Trash2, ListChecks, Star, Users, Search, Landmark,
  AlertTriangle, Database,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { api, saveScanId, getSavedKeys, addSavedKey, removeSavedKey, setActiveScan, getActiveScan, clearActiveScan } from "@/lib/api";
import SocialAccountsInput from "@/components/scan/SocialAccountsInput";
import OwnerConcernsInput from "@/components/scan/OwnerConcernsInput";
import { CATEGORIES, US_STATES, SCAN_STAGES } from "@/lib/constants";

const THRESH = [18, 34, 50, 66, 80, 90];

export const PROV = {
  VERIFIED: { label: "Verified", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  PUBLIC_SOURCE: { label: "Public source", cls: "text-sky-700 bg-sky-50 border-sky-200" },
  AI_SUGGESTED: { label: "AI-suggested", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  USER_PROVIDED: { label: "You provided", cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  USER_CONFIRMED: { label: "You confirmed", cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  UNVERIFIED: { label: "Unverified", cls: "text-slate-600 bg-slate-100 border-slate-200" },
  UNABLE_TO_VERIFY: { label: "Unable to verify", cls: "text-slate-600 bg-slate-100 border-slate-200" },
  NOT_FOUND: { label: "Not found", cls: "text-slate-600 bg-slate-100 border-slate-200" },
  NOT_CONFIGURED: { label: "Not configured", cls: "text-slate-600 bg-slate-100 border-slate-200" },
  CONFIRMED: { label: "Confirmed", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  LIKELY: { label: "Likely", cls: "text-sky-700 bg-sky-50 border-sky-200" },
  UNKNOWN: { label: "Unknown", cls: "text-slate-500 bg-slate-100 border-slate-200" },
};
export const WEB_STATUS = {
  OFFICIAL_WEBSITE_FOUND: "Official website found",
  LIKELY_OFFICIAL: "Likely official website",
  POSSIBLE: "Possible website — needs confirmation",
  NO_WEBSITE_FOUND: "No website found",
  UNABLE_TO_VERIFY: "Unable to verify",
  USER_PROVIDED: "Provided by you",
  NONE: "No website",
};
const CONF = {
  HIGH: { label: "High confidence", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  MEDIUM: { label: "Moderate confidence", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  LOW: { label: "Low confidence", cls: "text-slate-600 bg-slate-100 border-slate-200" },
};

export function ProvBadge({ status, testid }) {
  const p = PROV[status] || PROV.UNVERIFIED;
  return <span data-testid={testid} className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${p.cls}`}>{p.label}</span>;
}

/* ---------- Multi-source evidence: confidence tiers + source matrix ---------- */
export const TIER = {
  VERIFIED: { label: "Verified", cls: "text-emerald-800 bg-emerald-100 border-emerald-300", desc: "Confirmed by an authoritative source." },
  STRONGLY_CORROBORATED: { label: "Strongly corroborated", cls: "text-teal-800 bg-teal-50 border-teal-300", desc: "Agreed on by two or more independent public sources." },
  LIKELY: { label: "Likely", cls: "text-sky-800 bg-sky-50 border-sky-300", desc: "Supported by one public source." },
  UNVERIFIED: { label: "AI-assisted", cls: "text-amber-800 bg-amber-50 border-amber-300", desc: "AI-suggested lead — not confirmed by a public source." },
  UNKNOWN: { label: "Not found", cls: "text-slate-600 bg-slate-100 border-slate-300", desc: "No source returned this value." },
};
const SYS_LABEL = { google_places: "Google Places", osm: "OpenStreetMap", ai: "AI discovery", web_search: "Web search", user: "You provided", other: "Other source" };
const MATRIX_FIELDS = [["name", "Name"], ["address", "Address"], ["city", "City"], ["state", "State"], ["zip", "ZIP"], ["phone", "Phone"], ["website", "Website"], ["category", "Category"]];
const TIER_TO_PROV = { VERIFIED: "VERIFIED", STRONGLY_CORROBORATED: "PUBLIC_SOURCE", LIKELY: "PUBLIC_SOURCE", UNVERIFIED: "AI_SUGGESTED", UNKNOWN: "UNVERIFIED" };
const tierToProv = (t) => TIER_TO_PROV[t] || "AI_SUGGESTED";

export function TierBadge({ tier, testid }) {
  const t = TIER[tier] || TIER.UNKNOWN;
  return <span data-testid={testid} title={t.desc} className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${t.cls}`}>{t.label}</span>;
}

function SourceMatrix({ matrix, testid }) {
  if (!matrix) return null;
  const rows = MATRIX_FIELDS.filter(([k]) => matrix[k] && matrix[k].value != null && matrix[k].value !== "");
  if (!rows.length) return <p className="text-sm text-slate-500">No field-level evidence was recorded.</p>;
  return (
    <div className="space-y-2.5" data-testid={testid}>
      {rows.map(([k, label]) => {
        const f = matrix[k];
        return (
          <div key={k} data-testid={`matrix-field-${k}`} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
                <div className="text-sm text-slate-800 break-words">{String(f.value)}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {f.conflict && <span data-testid={`matrix-conflict-${k}`} className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">Conflict</span>}
                <TierBadge tier={f.tier} />
              </div>
            </div>
            <div className="mt-2 space-y-1 border-t border-slate-100 pt-2">
              {(f.sources || []).map((s, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-[12px]">
                  <span className="flex items-center gap-1.5 min-w-0 text-slate-500">
                    {s.supports ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> : <X className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                    <span className="truncate">{SYS_LABEL[s.source_system] || s.source}</span>
                  </span>
                  <span className={`truncate max-w-[48%] text-right ${s.supports ? "text-slate-600" : "text-red-600 line-through"}`}>{String(s.value)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SourcesAttempted({ identity, testid }) {
  const att = identity?.sources_attempted || [];
  if (!att.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {att.map((a) => {
        const st = !a.queried ? "unavailable" : a.status === "ERROR" ? "failed" : a.returned ? "ok" : "none";
        const cls = { ok: "text-emerald-700 bg-emerald-50 border-emerald-200", none: "text-slate-500 bg-slate-100 border-slate-200", unavailable: "text-slate-400 bg-slate-50 border-slate-200", failed: "text-red-700 bg-red-50 border-red-200" }[st];
        const note = { ok: `returned ${a.returned || 0}`, none: "no result", unavailable: "not connected", failed: "error" }[st];
        return (
          <span key={a.source} data-testid={`src-att-${(a.source || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${cls}`}>
            {st === "ok" ? <CheckCircle2 className="w-3 h-3" /> : st === "failed" ? <X className="w-3 h-3" /> : <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50" />}
            {a.source} <span className="opacity-70">· {note}</span>
          </span>
        );
      })}
    </div>
  );
}

function IdentityEvidence({ identity, matrix, testid }) {
  const [open, setOpen] = useState(false);
  const mtx = matrix || identity?.primary?.source_matrix;
  if (!identity && !mtx) return null;
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4" data-testid={testid}>
      {identity?.degradation && <p className="text-[12px] text-slate-600 leading-relaxed" data-testid={`${testid}-degradation`}>{identity.degradation}</p>}
      {identity?.sources_attempted?.length ? (
        <div className="mt-2.5">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">Sources checked</div>
          <SourcesAttempted identity={identity} testid={`${testid}-attempted`} />
        </div>
      ) : null}
      {mtx && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          <button type="button" data-testid={`${testid}-matrix-toggle`} onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900">
            <ListChecks className="w-4 h-4" /> Source matrix — what each source said {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {open && <div className="mt-3"><SourceMatrix matrix={mtx} testid={`${testid}-matrix`} /></div>}
        </div>
      )}
    </div>
  );
}

const STALE = {
  fresh: "text-emerald-700 bg-emerald-50 border-emerald-200",
  recent: "text-emerald-700 bg-emerald-50 border-emerald-200",
  aging: "text-amber-700 bg-amber-50 border-amber-200",
  stale: "text-red-700 bg-red-50 border-red-200",
  unknown: "text-slate-600 bg-slate-100 border-slate-200",
};
export function StalenessPill({ staleness, testid }) {
  if (!staleness) return null;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STALE[staleness.level] || STALE.unknown}`}>
      <Clock className="w-3 h-3" /> {staleness.label}
    </span>
  );
}

export function TrailList({ trail, testid }) {
  if (!trail?.length) return null;
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {trail.map((t, i) => {
        const ok = ["VERIFIED", "PUBLIC_SOURCE", "USER_CONFIRMED", "USER_PROVIDED"].includes(t.status);
        return (
          <div key={i} className="flex items-start gap-2 text-sm" data-testid={`${testid}-item-${i}`}>
            <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${ok ? "text-emerald-500" : "text-slate-300"}`} />
            <div className="min-w-0 flex-1">
              <span className="text-slate-700 font-medium">{t.check}</span>
              {t.detail && <span className="text-slate-400"> — {t.detail}</span>}
              {t.url && <a href={t.url.startsWith("http") ? t.url : `https://${t.url}`} target="_blank" rel="noreferrer" className="ml-1 text-indigo-600 hover:underline inline-flex items-center gap-0.5 break-all">{t.url} <ExternalLink className="w-3 h-3" /></a>}
              {t.checked_at && <span className="text-[10px] text-slate-400 ml-1">· checked {String(t.checked_at).slice(0, 10)}</span>}
            </div>
            <ProvBadge status={t.status} />
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Label className="text-sm font-medium text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</Label>
        {hint && (
          <Tooltip>
            <TooltipTrigger asChild><button type="button" className="text-slate-400 hover:text-slate-600"><Info className="w-3.5 h-3.5" /></button></TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs">{hint}</TooltipContent>
          </Tooltip>
        )}
      </div>
      {children}
    </div>
  );
}

export default function ScanWizard() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const rescanLoaded = useRef(false);
  const [step, setStep] = useState("form");
  const [scanId, setScanId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [websiteMode, setWebsiteMode] = useState("known"); // known | none | unknown
  const [mode, setMode] = useState("bi");
  const [draft, setDraft] = useState(null);
  const [corrections, setCorrections] = useState({});
  const [savedFrom, setSavedFrom] = useState(null);      // saved business the draft was loaded from
  const [savedKey, setSavedKey] = useState(null);        // business_key once saved this session
  const [savingBiz, setSavingBiz] = useState(false);
  const [prevDraft, setPrevDraft] = useState(null);      // previous candidate for re-discover compare
  const [savedList, setSavedList] = useState([]);
  const [discJob, setDiscJob] = useState(null);          // live streaming discovery job state
  const discTimer = useRef(null);

  const [biz, setBiz] = useState({
    name: "", website_url: "", category: "", city: "", state: "HI",
    google_url: "", facebook_url: "", instagram_url: "",
  });
  const [manual, setManual] = useState({ review_count: "", rating: "", review_response_rate: "" });
  const [competitors, setCompetitors] = useState([{ name: "", review_count: "", rating: "" }]);
  const set = (k, v) => setBiz((b) => ({ ...b, [k]: v }));

  useEffect(() => {
    const keys = getSavedKeys();
    if (!keys.length) return;
    api.get("/saved-businesses", { params: { keys: keys.join(",") } })
      .then(({ data }) => setSavedList(data.businesses || []))
      .catch(() => {});
  }, []);

  // Reconnect to an in-flight scan after a refresh/close: the scan keeps running server-side.
  const reconnected = useRef(false);
  useEffect(() => {
    if (reconnected.current) return;
    reconnected.current = true;
    const active = getActiveScan();
    if (!active?.id) return;
    api.get(`/scan/${active.id}/status`)
      .then(({ data }) => {
        if (["queued", "running", "pending"].includes(data.status)) {
          setScanId(active.id);
          if (active.biz) setBiz((b) => ({ ...b, ...active.biz }));
          setStep("scanning");
        } else if (data.status === "complete") {
          clearActiveScan();
          nav(`/report/${active.id}`);
        } else {
          clearActiveScan();
        }
      })
      .catch(() => clearActiveScan());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSaved = (sb) => {
    setDraft(sb.profile);
    setCorrections({});
    setSavedFrom(sb);
    setSavedKey(sb.business_key);
    setBiz((b) => ({ ...b, name: sb.name || b.name, city: sb.city || b.city, state: sb.state || b.state }));
    setMode("bi");
    setStep("confirm");
  };

  // Re-scan from "My Businesses": load the previously confirmed identity, then go straight to the
  // identity-confirmation gate (YES / EDIT / RE-DISCOVER) — never bypassing confirmation.
  useEffect(() => {
    const key = params.get("business");
    if (!key || rescanLoaded.current) return;
    rescanLoaded.current = true;
    api.get(`/saved-businesses/${encodeURIComponent(key)}`)
      .then(({ data }) => {
        if (data?.profile) { loadSaved(data); toast.message("Loaded your saved business — confirm the identity to re-scan."); }
      })
      .catch(() => toast.error("Could not load that saved business."));
  }, [params]);

  const removeSaved = async (key) => {
    removeSavedKey(key);
    setSavedList((l) => l.filter((x) => x.business_key !== key));
    try { await api.delete(`/saved-businesses/${encodeURIComponent(key)}`); } catch (e) { /* noop */ }
    toast.success("Removed saved business.");
  };

  const saveBusiness = async () => {
    setSavingBiz(true);
    try {
      const { data } = await api.post("/saved-businesses", { profile: draft, corrections });
      addSavedKey(data.business_key);
      setSavedKey(data.business_key);
      toast.success("Business saved for faster future scans.");
    } catch (e) {
      toast.error("Could not save this business.");
    } finally { setSavingBiz(false); }
  };

  const reDiscover = async () => {
    setPrevDraft(draft);
    setSubmitting(true);
    setStep("discovering");
    try {
      const cur = draft?.website?.value;
      const { data } = await api.post("/discovery", {
        name: mergedVal("name"), city: mergedVal("city"), state: mergedVal("state"),
        website_mode: cur ? "known" : "unknown", website_url: cur || "",
      });
      setStep("rediscover");
      setDraft(data);
      setCorrections({});
    } catch (e) {
      toast.error("Re-discovery failed. Keeping the current identity.");
      setStep("confirm");
    } finally { setSubmitting(false); }
  };

  const keepPrevious = () => { setDraft(prevDraft); setPrevDraft(null); setStep("confirm"); };
  const useNew = () => { setPrevDraft(null); setSavedKey(null); setStep("confirm"); };

  // ---- Discovery (streaming, multi-source) ----
  useEffect(() => () => { if (discTimer.current) clearInterval(discTimer.current); }, []);

  const routeAfterDiscovery = (d) => {
    setDraft(d);
    setCorrections({});
    if (d.entity?.ambiguous && (d.entity?.candidates || []).length > 1) setStep("entity");
    else if ((d.website?.candidates || []).length > 1 && websiteMode !== "known") setStep("website");
    else setStep("confirm");
  };

  const pollDiscovery = (jobId) => {
    if (discTimer.current) clearInterval(discTimer.current);
    let misses = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/discovery/job/${jobId}`);
        setDiscJob(data);
        if (data.status === "complete" && data.draft) {
          clearInterval(discTimer.current); discTimer.current = null;
          setSubmitting(false);
          routeAfterDiscovery(data.draft);
        } else if (data.status === "failed") {
          clearInterval(discTimer.current); discTimer.current = null;
          setSubmitting(false);
          toast.error("Discovery failed. You can still enter details manually.");
          setDraft(minimalDraft(biz, websiteMode)); setStep("confirm");
        }
      } catch (e) {
        if (++misses > 8) {
          clearInterval(discTimer.current); discTimer.current = null;
          setSubmitting(false);
          toast.error("Discovery failed. You can still enter details manually.");
          setDraft(minimalDraft(biz, websiteMode)); setStep("confirm");
        }
      }
    };
    tick(); // immediate poll (instant on a cache hit)
    discTimer.current = setInterval(tick, 1200);
  };

  const runDiscovery = async () => {
    setSubmitting(true);
    setSavedFrom(null);
    setSavedKey(null);
    setPrevDraft(null);
    setDiscJob(null);
    setStep("discovering");
    try {
      const { data } = await api.post("/discovery/start", {
        name: biz.name, city: biz.city, state: biz.state,
        website_mode: websiteMode, website_url: websiteMode === "known" ? biz.website_url : "",
      });
      pollDiscovery(data.job_id);
    } catch (err) {
      setSubmitting(false);
      toast.error("Discovery failed. You can still enter details manually.");
      setDraft(minimalDraft(biz, websiteMode));
      setStep("confirm");
    }
  };

  const submitForm = (e) => {
    e.preventDefault();
    if (!biz.name.trim()) { toast.error("Please enter your business name."); return; }
    if (websiteMode === "known" && !biz.website_url.trim()) {
      toast.error("Enter your website, or choose “No website” / “I don't know”."); return;
    }
    runDiscovery();
  };

  const pickEntity = (c) => {
    // The user is DISAMBIGUATING (choosing which discovered business is theirs) — not editing values.
    // Carry the picked candidate through the draft with its TRUE provenance (e.g. PUBLIC_SOURCE for an
    // OpenStreetMap match); never relabel a discovered value as USER_CONFIRMED just because it was tapped.
    setDraft((d) => {
      if (!d) return d;
      const ft = c.field_tiers || {};
      const nameProv = tierToProv(ft.name || c.confidence_tier);
      const addrTier = ft.address || "UNKNOWN";
      const locProv = tierToProv(c.address ? addrTier : (ft.city || "UNKNOWN"));
      return {
        ...d,
        entity: { ...(d.entity || {}), primary: c },
        identity: d.identity ? { ...d.identity, primary: c, confidence_tier: c.confidence_tier } : d.identity,
        name: { ...(d.name || {}), value: c.name, source: nameProv, status: nameProv, tier: ft.name },
        location: { ...(d.location || {}), city: c.city, state: c.state, address: c.address || null,
          address_tier: addrTier, source: locProv, status: locProv },
        category: { ...(d.category || {}), display_type: c.category || d.category?.display_type,
          detected_label: c.category || d.category?.detected_label,
          status: d.category?.status || tierToProv(ft.category) },
        identity_confidence: { ...(d.identity_confidence || {}), tier: c.confidence_tier },
      };
    });
    // Clear any identity fields previously placed in corrections so provenance is not mislabeled.
    setCorrections((x) => {
      const c = { ...x };
      delete c.name; delete c.city; delete c.state; delete c.address; delete c.category;
      return c;
    });
    if ((draft?.website?.candidates || []).length > 1 && websiteMode !== "known") setStep("website");
    else setStep("confirm");
  };

  const pickWebsite = (choice) => {
    if (choice === "none") setCorrections((x) => ({ ...x, website_url: "" }));
    else if (choice === "unknown") { /* leave discovered as-is */ }
    else setCorrections((x) => ({ ...x, website_url: choice }));
    setStep("confirm");
  };

  const confirmAndScan = () => { setStep("revcomp"); };

  // opts: { manual: {review_count,rating,review_response_rate}|null, competitors: [CompetitorInput] }
  const startScan = async (opts = {}) => {
    setSubmitting(true);
    const manualData = opts.manual || (
      (manual.review_count || manual.rating || manual.review_response_rate) ? {
        review_count: manual.review_count ? Number(manual.review_count) : null,
        rating: manual.rating ? Number(manual.rating) : null,
        review_response_rate: manual.review_response_rate ? Number(manual.review_response_rate) : null,
      } : null);
    const confirmedComps = (opts.competitors || []).filter((c) => (c.name || "").trim());
    try {
      let data;
      if (mode === "bi") {
        ({ data } = await api.post("/scan/bi", {
          name: mergedVal("name"), city: mergedVal("city"), state: mergedVal("state"),
          category: mergedCategory(), profile: draft, corrections,
          manual: manualData, competitors: confirmedComps,
          social_presence: opts.social_presence, social_accounts: opts.social_accounts || [],
          owner_concerns: opts.owner_concerns || null,
        }));
      } else {
        const business = {
          name: mergedVal("name"), category: mergedCategory() || "Local business",
          city: mergedVal("city"), state: mergedVal("state"), website_url: mergedWebsite(),
          google_url: biz.google_url, facebook_url: biz.facebook_url, instagram_url: biz.instagram_url,
          social_presence: opts.social_presence, social_accounts: opts.social_accounts || [],
          owner_concerns: opts.owner_concerns || null,
          profile: draft, corrections,
        };
        const payload = { business, manual: manualData, competitors: confirmedComps };
        ({ data } = await api.post("/scan", payload));
      }
      saveScanId(data.id);
      setScanId(data.id);
      setActiveScan(data.id, { name: mergedVal("name"), city: mergedVal("city"), state: mergedVal("state"), website_url: mergedWebsite() }, mode);
      setStep("scanning");
    } catch (err) {
      toast.error("Could not start the scan. Please try again.");
      setSubmitting(false);
    }
  };

  // merged value = user correction beats discovered
  const mergedVal = (k) => {
    if (corrections[k] != null && corrections[k] !== "") return corrections[k];
    if (k === "name") return draft?.name?.value || biz.name;
    if (k === "city") return draft?.location?.city || biz.city;
    if (k === "state") return draft?.location?.state || biz.state;
    return biz[k];
  };
  const mergedCategory = () => corrections.category || draft?.category?.internal_label || biz.category || "";
  const mergedWebsite = () => {
    if (corrections.website_url != null) return corrections.website_url;
    return draft?.website?.value || (websiteMode === "known" ? biz.website_url : "");
  };

  const addCompetitor = () => { if (competitors.length < 5) setCompetitors((c) => [...c, { name: "", review_count: "", rating: "" }]); };
  const updateComp = (i, k, v) => setCompetitors((c) => c.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const removeComp = (i) => setCompetitors((c) => c.filter((_, j) => j !== i));

  return (
    <TooltipProvider delayDuration={150}>
      <div className="min-h-screen bg-slate-50">
        <header className="border-b border-slate-200 bg-white">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5" data-testid="wizard-logo">
              <span className="grid place-items-center w-9 h-9 rounded-lg bg-slate-900 text-white"><ScanSearch className="w-5 h-5" /></span>
              <span className="font-heading font-bold text-[15px] text-slate-900">Opportunity<span className="text-indigo-600">Scanner</span></span>
            </Link>
            <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Exit</Link>
          </div>
        </header>

        <AnimatePresence mode="wait">
          {step === "form" ? (
            <FormStep key="form" mode={mode} setMode={setMode} biz={biz} set={set} websiteMode={websiteMode}
              setWebsiteMode={setWebsiteMode} onSubmit={submitForm} submitting={submitting}
              advanced={advanced} setAdvanced={setAdvanced} manual={manual} setManual={setManual}
              competitors={competitors} addCompetitor={addCompetitor} updateComp={updateComp} removeComp={removeComp}
              savedList={savedList} onLoadSaved={loadSaved} onRemoveSaved={removeSaved} />
          ) : step === "discovering" ? (
            <DiscoveringScreen key="disc" job={discJob} name={mergedVal("name") || biz.name} loc={[mergedVal("city") || biz.city, mergedVal("state") || biz.state].filter(Boolean).join(", ")} />
          ) : step === "entity" ? (
            <EntityConfirm key="entity" draft={draft} onPick={pickEntity} onBack={() => setStep("form")} />
          ) : step === "website" ? (
            <WebsiteConfirm key="website" draft={draft} onPick={pickWebsite} onBack={() => setStep(draft?.entity?.ambiguous ? "entity" : "form")} />
          ) : step === "rediscover" ? (
            <RediscoverCompare key="redisc" previous={prevDraft} current={draft} onKeepPrevious={keepPrevious} onUseNew={useNew} />
          ) : step === "confirm" ? (
            <IdentityConfirm key="confirm" draft={draft} corrections={corrections} mode={mode} submitting={submitting}
              onYes={confirmAndScan} onEdit={() => setStep("edit")} onRestart={() => setStep("form")}
              onReDiscover={reDiscover} onSave={saveBusiness} saved={!!savedKey} saving={savingBiz} savedFrom={savedFrom} />
          ) : step === "edit" ? (
            <EditProfile key="edit" draft={draft} corrections={corrections} setCorrections={setCorrections}
              onDone={() => setStep("confirm")} />
          ) : step === "revcomp" ? (
            <ReviewCompetitiveStep key="revcomp" mode={mode}
              identity={{ name: mergedVal("name"), city: mergedVal("city"), state: mergedVal("state"),
                category: mergedCategory(), website_url: mergedWebsite(),
                description: draft?.description?.value || "" }}
              onBack={() => setStep("confirm")} submitting={submitting}
              onConfirm={(payload) => startScan(payload)} />
          ) : step === "cancelled" ? (
            <CancelledScreen key="cancelled" onRestart={() => setStep("form")} />
          ) : (
            <ScanningScreen key="scan" scanId={scanId} biz={{ name: mergedVal("name"), city: mergedVal("city"), state: mergedVal("state"), website_url: mergedWebsite() }}
              onDone={() => { clearActiveScan(); nav(`/report/${scanId}`); }}
              onCancel={() => { clearActiveScan(); setStep("cancelled"); setSubmitting(false); }}
              onFail={() => { clearActiveScan(); toast.error("The scan could not be completed. Please try again."); setStep("confirm"); setSubmitting(false); }} />
          )}
        </AnimatePresence>
      </div>
    </TooltipProvider>
  );
}

function minimalDraft(biz, websiteMode) {
  return {
    name: { value: biz.name, source: "USER_PROVIDED", status: "USER_PROVIDED" },
    location: { city: biz.city, state: biz.state, source: "USER_PROVIDED", status: "USER_PROVIDED" },
    category: { internal_key: "default", internal_label: biz.category || "Local business", display_type: biz.category || "Local Business", industry: "Local Business", status: "UNKNOWN", confidence: 0.3, source: "User provided" },
    description: { value: "", source: "User provided", status: "USER_PROVIDED" },
    website: websiteMode === "known"
      ? { status: "USER_PROVIDED", value: biz.website_url, verification_status: "USER_PROVIDED", candidates: [] }
      : { status: websiteMode === "none" ? "NONE" : "UNABLE_TO_VERIFY", value: null, verification_status: "UNABLE_TO_VERIFY", candidates: [] },
    entity: { primary: { name: biz.name }, candidates: [], ambiguous: false, verified: false },
    sources: [{ name: "You provided", type: "user", status: "USER_PROVIDED", confidence: 1 }],
    identity_confidence: { level: "LOW", score: 0.4 },
    connectors: { google_places: false, web_search: false },
  };
}

/* ---------------- Step 1: form ---------------- */
function FormStep({ mode, setMode, biz, set, websiteMode, setWebsiteMode, onSubmit, submitting, advanced, setAdvanced, manual, setManual, competitors, addCompetitor, updateComp, removeComp, savedList = [], onLoadSaved, onRemoveSaved }) {
  const opt = (val, icon, label, sub) => {
    const Icon = icon;
    const active = websiteMode === val;
    return (
      <button type="button" data-testid={`website-option-${val}`} onClick={() => setWebsiteMode(val)}
        className={`flex items-start gap-2.5 rounded-lg border px-3.5 py-3 text-sm font-medium text-left transition-colors ${active ? "border-indigo-500 bg-indigo-50 text-indigo-800 ring-1 ring-indigo-500" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
        <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${active ? "text-indigo-600" : "text-slate-400"}`} />
        <span><span className="block">{label}</span><span className="block text-[11px] font-normal text-slate-400">{sub}</span></span>
      </button>
    );
  };
  return (
    <motion.div key="form" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }} className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
      <p className="text-indigo-600 font-semibold text-xs uppercase tracking-[0.2em] mb-2">Step 1 · Find your business</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Let's identify your business first</h1>
      <p className="mt-3 text-slate-600">Enter the name and location. We'll discover the business, show you what we found, and let you confirm it before the full scan — so we always analyze the right business.</p>

      {savedList.length > 0 && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="saved-businesses">
          <div className="flex items-center gap-2 mb-3">
            <Bookmark className="w-4 h-4 text-indigo-600" />
            <p className="text-sm font-semibold text-slate-800">Your saved businesses</p>
            <span className="text-xs text-slate-400">— confirm before each scan</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {savedList.map((sb) => (
              <div key={sb.business_key} data-testid={`saved-business-${sb.business_key}`}
                className="group relative rounded-lg border border-slate-200 p-3.5 hover:border-indigo-400 hover:ring-1 hover:ring-indigo-400 transition-colors">
                <button type="button" onClick={() => onLoadSaved(sb)} className="w-full text-left" data-testid={`saved-load-${sb.business_key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-900 truncate">{sb.name}</span>
                    <StalenessPill staleness={sb.staleness} testid={`saved-stale-${sb.business_key}`} />
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">{[sb.category_display, [sb.city, sb.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</div>
                  <div className="text-[11px] text-slate-400 mt-1">{sb.scan_count || 0} previous scan{sb.scan_count === 1 ? "" : "s"} · tap to confirm &amp; scan</div>
                </button>
                <button type="button" onClick={() => onRemoveSaved(sb.business_key)} data-testid={`saved-remove-${sb.business_key}`}
                  className="absolute top-2 right-2 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="scan-mode-toggle">
        <button type="button" data-testid="scan-mode-bi" onClick={() => setMode("bi")}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${mode === "bi" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>360° Scan</button>
        <button type="button" data-testid="scan-mode-standard" onClick={() => setMode("standard")}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${mode === "standard" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>Detailed scan</button>
      </div>

      <form onSubmit={onSubmit} className="mt-8 space-y-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 shadow-sm space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Business name" required hint="Your official business name as customers know it.">
              <Input data-testid="input-name" value={biz.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Aloha Grill" />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Field label="City" hint="The city your business primarily serves.">
                  <Input data-testid="input-city" value={biz.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Honolulu" />
                </Field>
              </div>
              <Field label="State">
                <Select value={biz.state} onValueChange={(v) => set("state", v)}>
                  <SelectTrigger data-testid="select-state"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Label className="text-sm font-medium text-slate-700">Do you have a website for this business?</Label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              {opt("known", Globe, "Yes — I know it", "Enter the URL")}
              {opt("none", Ban, "No — we don't have one", "We'll double-check")}
              {opt("unknown", HelpCircle, "I don't know", "Find out for me")}
            </div>
            {websiteMode === "known" ? (
              <Input data-testid="input-website" value={biz.website_url} onChange={(e) => set("website_url", e.target.value)} placeholder="e.g. www.yourbusiness.com" autoFocus />
            ) : (
              <div data-testid="website-note" className="rounded-lg bg-indigo-50 border border-indigo-100 px-4 py-3 text-sm text-indigo-800">
                {websiteMode === "none"
                  ? "No problem — we'll still check public sources in case one exists, and flag a missing site as a top opportunity."
                  : "We'll research whether an official website appears to exist and show you what we find to confirm."}
              </div>
            )}
          </div>

          {mode === "standard" && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-xs text-slate-500">
              Detailed scan: after you confirm the business identity, you can optionally add your review &amp; competitor data.
            </div>
          )}
        </div>

        <Button type="submit" data-testid="submit-scan" disabled={submitting}
          className="w-full rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold py-6 text-base active:scale-[0.99] transition-transform">
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Discovering…</> : <>Discover my business <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
        <p className="text-center text-xs text-slate-400">We only use public sources and never fabricate data. AI suggestions are shown as suggestions, not verified facts.</p>
      </form>
    </motion.div>
  );
}

/* ---------------- Discovering (live, multi-source streaming board) ---------------- */
const PSTATUS = {
  QUEUED: { label: "Queued", cls: "text-slate-500 bg-slate-100 border-slate-200", dot: "bg-slate-300" },
  SEARCHING: { label: "Searching…", cls: "text-indigo-700 bg-indigo-50 border-indigo-200", dot: "bg-indigo-500 animate-pulse" },
  FOUND: { label: "Found", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", dot: "bg-emerald-500" },
  NO_MATCH: { label: "No match", cls: "text-slate-500 bg-slate-100 border-slate-200", dot: "bg-slate-300" },
  UNAVAILABLE: { label: "Not connected", cls: "text-amber-700 bg-amber-50 border-amber-200", dot: "bg-amber-400" },
  ERROR: { label: "Error", cls: "text-red-700 bg-red-50 border-red-200", dot: "bg-red-500" },
  COMPLETED: { label: "Done", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", dot: "bg-emerald-500" },
};
const SRC_ICON = {
  search_engine: Search, maps: MapPin, ai: Sparkles, website: Globe,
  directory: ListChecks, review: Star, social: Users, government: Landmark,
};
const DISC_PHASES = ["Searching", "Candidates", "Cross-checking", "Resolving", "Snapshot"];

function ProviderRow({ p }) {
  const st = PSTATUS[p.status] || PSTATUS.QUEUED;
  const Icon = SRC_ICON[p.source_type] || Database;
  const searching = p.status === "SEARCHING";
  return (
    <div data-testid={`disc-provider-${p.key}`} className={`flex items-start gap-3 rounded-xl border p-3.5 transition-colors ${searching ? "border-indigo-200 bg-indigo-50/40" : "border-slate-200 bg-white"}`}>
      <span className={`grid place-items-center w-9 h-9 rounded-lg shrink-0 ${searching ? "bg-indigo-100 text-indigo-600" : p.status === "FOUND" ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
        {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="font-semibold text-sm text-slate-800 truncate">{p.name}</p>
          <span data-testid={`disc-status-${p.key}`} className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shrink-0 ${st.cls}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{st.label}{p.results_count > 0 ? ` · ${p.results_count}` : ""}
          </span>
        </div>
        <p className="text-[12px] text-slate-500 mt-0.5">{p.note || p.detail}</p>
        {p.status === "UNAVAILABLE" && p.verify_url && (
          <a href={p.verify_url} target="_blank" rel="noreferrer" data-testid={`disc-verify-${p.key}`}
            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:underline">
            Check manually <ExternalLink className="w-3 h-3" />
          </a>
        )}
        {p.attribution && <p className="text-[10px] text-slate-400 mt-1">{p.attribution}</p>}
      </div>
    </div>
  );
}

function DiscoveringScreen({ job, name, loc }) {
  // No job yet (or re-discover path) -> lightweight animated state.
  if (!job || !job.providers) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto px-5 sm:px-8 py-20 text-center" data-testid="discovering-screen">
        <div className="relative grid place-items-center h-40 mb-6">
          <div className="absolute w-40 h-40 rounded-full border border-slate-200" />
          <div className="absolute w-40 h-40 rounded-full animate-ping-slow bg-indigo-500/10" />
          <div className="relative grid place-items-center w-14 h-14 rounded-full bg-slate-900 text-white shadow-lg"><Radar className="w-6 h-6 animate-pulse" /></div>
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Researching your business…</h1>
        <p className="mt-2 text-slate-500">{name}{loc ? ` · ${loc}` : ""}</p>
        <p className="mt-4 text-sm text-slate-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-indigo-600" /> Checking public sources, websites &amp; business type…</p>
      </motion.div>
    );
  }
  const providers = job.providers || [];
  const phaseIdx = job.phase?.index || 1;
  const candidates = job.candidates || [];
  const p1 = providers.filter((p) => p.phase === 1);
  const p2 = providers.filter((p) => p.phase === 2);
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-12" data-testid="discovering-screen">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-11 h-11 rounded-xl bg-slate-900 text-white shrink-0"><Radar className="w-5 h-5 animate-pulse" /></span>
        <div className="min-w-0">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Researching your business…</h1>
          <p className="text-slate-500 text-sm truncate">{name}{loc ? ` · ${loc}` : ""}</p>
        </div>
      </div>

      {/* Phase progress */}
      <div className="mt-6 flex items-center gap-1.5" data-testid="disc-phases">
        {DISC_PHASES.map((label, i) => {
          const n = i + 1;
          const done = phaseIdx > n;
          const active = phaseIdx === n;
          return (
            <div key={label} className="flex-1 min-w-0">
              <div className={`h-1.5 rounded-full transition-colors ${done ? "bg-emerald-500" : active ? "bg-indigo-500" : "bg-slate-200"}`} />
              <p className={`mt-1.5 text-[10px] font-semibold uppercase tracking-wide truncate ${active ? "text-indigo-600" : done ? "text-emerald-600" : "text-slate-400"}`}>{label}</p>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-sm text-slate-500 inline-flex items-center gap-2" data-testid="disc-phase-label">
        {job.status === "complete" ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />}
        {job.phase?.label || "Working…"}
      </p>

      {/* Candidates found so far */}
      {candidates.length > 0 && (
        <div className="mt-6" data-testid="disc-candidates">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Candidate businesses discovered</p>
          <div className="space-y-2">
            {candidates.slice(0, 4).map((c, i) => (
              <div key={i} data-testid={`disc-candidate-${i}`} className="rounded-lg border border-slate-200 bg-white p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-slate-800 truncate">{c.name}</p>
                  <p className="text-[12px] text-slate-500 truncate">{[c.category, [c.address || c.city, c.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ") || "Details pending"}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{(c.source_systems || []).map((s) => SYS_LABEL[s] || s).join(", ") || "AI only"}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.has_conflict && <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">Conflict</span>}
                  <TierBadge tier={c.confidence_tier} testid={`disc-candidate-tier-${i}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Provider status board */}
      <div className="mt-6 space-y-2.5" data-testid="disc-providers">
        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Sources · Phase 1 — identity &amp; maps</p>
        {p1.map((p) => <ProviderRow key={p.key} p={p} />)}
        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold pt-2">Sources · Phase 2 — cross-source verification</p>
        {p2.map((p) => <ProviderRow key={p.key} p={p} />)}
      </div>
      <p className="mt-5 text-center text-xs text-slate-400">We only search legitimate public sources and never fabricate data. A source that is not connected is shown honestly, and the research continues with the rest.</p>
    </motion.div>
  );
}

/* ---------------- Which business did you mean? ---------------- */
function EntityConfirm({ draft, onPick, onBack }) {
  const cands = draft?.entity?.candidates || [];
  const identity = draft?.identity;
  const [openMatrix, setOpenMatrix] = useState(null);
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }} className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="entity-confirm">
      <p className="text-indigo-600 font-semibold text-xs uppercase tracking-[0.2em] mb-2">Confirm business</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Which business did you mean?</h1>
      <p className="mt-3 text-slate-600">We searched multiple public sources and found more than one possible match. Pick the correct one — each shows how confident we are and where the evidence came from.</p>
      <div className="mt-8 space-y-3">
        {cands.map((c, i) => (
          <div key={i} data-testid={`entity-candidate-${i}`}
            className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:border-indigo-300">
            <button type="button" data-testid={`entity-pick-${i}`} onClick={() => onPick(c)} className="w-full text-left">
              <div className="flex items-center justify-between gap-3">
                <p className="font-heading font-semibold text-slate-900">{c.name}</p>
                <div className="flex items-center gap-2 shrink-0">
                  {c.has_conflict && <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">Conflict</span>}
                  <TierBadge tier={c.confidence_tier} testid={`entity-tier-${i}`} />
                  {c.confidence != null && <span className="text-[11px] font-semibold text-slate-500">{Math.round(c.confidence * 100)}%</span>}
                </div>
              </div>
              <p className="mt-1 text-sm text-slate-500">
                {[c.category, [c.address || c.city, c.state].filter(Boolean).join(", "), c.website, c.phone].filter(Boolean).join(" · ") || "Limited details available"}
              </p>
              {c.evidence && <p className="mt-1 text-[11px] text-slate-400">{c.evidence}</p>}
              <p className="mt-1.5 text-[11px] text-slate-400">Sources: {(c.source_systems || []).map((s) => SYS_LABEL[s] || s).join(", ") || "AI only"}</p>
            </button>
            {c.source_matrix && (
              <div className="mt-2.5 pt-2.5 border-t border-slate-100">
                <button type="button" data-testid={`entity-matrix-toggle-${i}`} onClick={() => setOpenMatrix(openMatrix === i ? null : i)}
                  className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-800">
                  <ListChecks className="w-3.5 h-3.5" /> Evidence by field {openMatrix === i ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                {openMatrix === i && <div className="mt-3"><SourceMatrix matrix={c.source_matrix} testid={`entity-matrix-${i}`} /></div>}
              </div>
            )}
          </div>
        ))}
      </div>
      {identity && (
        <div className="mt-3">
          {identity.degradation && <p className="text-[12px] text-slate-500" data-testid="entity-degradation">{identity.degradation}</p>}
          <div className="mt-2"><SourcesAttempted identity={identity} testid="entity-sources-attempted" /></div>
        </div>
      )}
      <button type="button" onClick={onBack} data-testid="entity-back" className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> None of these — edit my search
      </button>
    </motion.div>
  );
}

/* ---------------- Which website belongs to this business? ---------------- */
function WebsiteConfirm({ draft, onPick, onBack }) {
  const cands = draft?.website?.candidates || [];
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }} className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="website-confirm">
      <p className="text-indigo-600 font-semibold text-xs uppercase tracking-[0.2em] mb-2">Confirm website</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Which website belongs to this business?</h1>
      <p className="mt-3 text-slate-600">We found more than one possible website. Confirm the correct one — or none. We only listed pages that actually load and mention the business.</p>
      <div className="mt-8 space-y-3">
        {cands.map((c, i) => (
          <button key={i} type="button" data-testid={`website-candidate-${i}`} onClick={() => onPick(c.url)}
            className="w-full text-left rounded-xl border border-slate-200 bg-white p-5 shadow-sm hover:border-indigo-400 hover:ring-1 hover:ring-indigo-400 transition-colors">
            <div className="flex items-center justify-between gap-3">
              <p className="font-medium text-indigo-700 break-all">{c.url}</p>
              <div className="flex items-center gap-2 shrink-0">
                <ProvBadge status={c.verification_status} />
                {c.confidence != null && <span className="text-[11px] font-semibold text-slate-500">{Math.round(c.confidence * 100)}%</span>}
              </div>
            </div>
            {c.evidence && <p className="mt-1 text-[11px] text-slate-400">{c.evidence}</p>}
          </button>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" onClick={() => onPick("none")} data-testid="website-none" className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:border-slate-300">None of these</button>
        <button type="button" onClick={() => onPick("unknown")} data-testid="website-idk" className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:border-slate-300">I don't know</button>
        <button type="button" onClick={onBack} className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Back</button>
      </div>
    </motion.div>
  );
}

/* ---------------- Online presence (Business Snapshot links) ---------------- */
function PresenceGroup({ icon, label, items, testid }) {
  if (!items || !items.length) return null;
  const Icon = icon;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <a key={i} href={it.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-medium text-sky-700 hover:bg-sky-100">
            {it.name || it.platform || it.domain} <ExternalLink className="w-3 h-3" />
          </a>
        ))}
      </div>
    </div>
  );
}

function OnlinePresence({ presence, snapshot }) {
  if (!presence) return null;
  const { configured, socials = [], reviews = [], directories = [], government = [] } = presence;
  const has = socials.length || reviews.length || directories.length || government.length;
  if (!has) {
    if (configured) return null;
    return (
      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4" data-testid="online-presence-empty">
        <p className="text-[12px] text-slate-500">A web-search provider is not connected, so social, review, directory and registry discovery ran on maps + AI only. Connect one (Admin) to automatically pull these public profiles.</p>
      </div>
    );
  }
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="online-presence">
      <p className="font-semibold text-sm text-slate-800 mb-3">What we found online <span className="text-[11px] font-normal text-slate-400">· public sources</span></p>
      <div className="space-y-3">
        <PresenceGroup icon={Users} label="Social profiles" items={socials} testid="presence-socials" />
        <PresenceGroup icon={Star} label="Review profiles" items={reviews} testid="presence-reviews" />
        <PresenceGroup icon={ListChecks} label="Directory listings" items={directories} testid="presence-directories" />
        <PresenceGroup icon={Landmark} label="Government / registry" items={government} testid="presence-government" />
      </div>
    </div>
  );
}

/* ---------------- Business Identity Confirmation ---------------- */
function IdentityConfirm({ draft, corrections, mode, submitting, onYes, onEdit, onRestart, onReDiscover, onSave, saved, saving, savedFrom }) {
  const [showTrail, setShowTrail] = useState(false);
  if (!draft) return null;
  const val = (k, path) => corrections[k] ?? path;
  const name = val("name", draft.name?.value);
  const city = val("city", draft.location?.city);
  const state = val("state", draft.location?.state);
  const cat = draft.category || {};
  const catDisplay = corrections.category || cat.display_type;
  const web = draft.website || {};
  const webUrl = corrections.website_url ?? web.value;
  const webStatus = corrections.website_url != null ? (corrections.website_url ? "USER_PROVIDED" : "NONE") : web.status;
  const desc = corrections.description ?? draft.description?.value;
  const ic = draft.identity_confidence || {};
  const conf = CONF[ic.level] || CONF.LOW;
  const sources = draft.sources || [];
  const trail = draft.trail || [];
  const identity = draft.identity;
  const tier = ic.tier || identity?.confidence_tier;
  const addr = corrections.address ?? draft.location?.address;
  const addrStatus = corrections.address != null ? "USER_CONFIRMED" : (draft.location?.source || "UNVERIFIED");
  const addrUnverified = !addr || !["VERIFIED", "PUBLIC_SOURCE", "USER_CONFIRMED", "USER_PROVIDED"].includes(addrStatus);

  const Row = ({ icon, label, children, status, testid }) => {
    const Icon = icon;
    return (
      <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={testid}>
        <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
          <div className="text-sm text-slate-800">{children}</div>
        </div>
        {status && <ProvBadge status={status} />}
      </div>
    );
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }} className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="identity-confirm">
      <p className="text-indigo-600 font-semibold text-xs uppercase tracking-[0.2em] mb-2">Confirm before scanning</p>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Is this the business you want to scan?</h1>
        <div className="flex items-center gap-2">
          {tier && <TierBadge tier={tier} testid="identity-tier" />}
          <span data-testid="identity-confidence" className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wide ${conf.cls}`}>{conf.label}</span>
        </div>
      </div>
      <p className="mt-3 text-slate-600">Confirm the details below. AI-suggested values are shown as suggestions, not verified facts — you can edit anything. Nothing is locked until you confirm.</p>

      {savedFrom && (
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50/70 p-4 flex items-start gap-3" data-testid="saved-staleness-banner">
          <Clock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">Loaded from your saved business · {savedFrom.staleness?.label}</p>
            <p className="text-xs text-amber-800/80 mt-0.5">Saved details speed things up but are never treated as currently verified. {savedFrom.staleness?.refresh_recommended ? "We recommend re-discovering to refresh the identity before scanning." : "Confirm it still looks right, or re-discover for the latest."}</p>
          </div>
          <Button data-testid="rediscover-banner-btn" variant="outline" size="sm" onClick={onReDiscover} disabled={submitting}
            className="shrink-0 border-amber-300 text-amber-800"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-discover</Button>
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="identity-card">
        <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-slate-900 text-white"><Building2 className="w-5 h-5" /></span>
          <div><div className="font-heading text-xl font-bold text-slate-900" data-testid="identity-name">{name}</div>
            <div className="text-sm text-slate-500">{[city, state].filter(Boolean).join(", ") || "Location not set"}</div></div>
        </div>
        <div className="mt-1">
          <Row icon={MapPin} label="Location" status={(corrections.city != null || corrections.state != null) ? "USER_CONFIRMED" : draft.location?.source} testid="identity-location">{[city, state].filter(Boolean).join(", ") || "—"}</Row>
          <Row icon={MapPin} label="Street address" status={addrStatus} testid="identity-address">
            {addr ? <span>{addr}</span> : <span className="text-slate-400">Not found in the public sources searched</span>}
          </Row>
          <Row icon={Sparkles} label="Business type" status={corrections.category ? "USER_CONFIRMED" : cat.status} testid="identity-category">
            {catDisplay || "Unknown"} <span className="text-slate-400">· {cat.industry}</span>
          </Row>
          <Row icon={Globe} label="Website" status={webStatus === "NONE" ? "USER_CONFIRMED" : (corrections.website_url != null ? "USER_CONFIRMED" : (web.verification_status || "UNVERIFIED"))} testid="identity-website">
            <span className="font-medium">{WEB_STATUS[webStatus] || webStatus}</span>
            {webUrl && <a href={webUrl.startsWith("http") ? webUrl : `https://${webUrl}`} target="_blank" rel="noreferrer" className="ml-2 text-indigo-600 hover:underline inline-flex items-center gap-0.5 break-all">{webUrl} <ExternalLink className="w-3 h-3" /></a>}
            {web.note && corrections.website_url == null && <div className="text-[11px] text-slate-400 mt-0.5">{web.note}</div>}
          </Row>
          {desc ? (
            <Row icon={Info} label="Description" status={corrections.description ? "USER_CONFIRMED" : draft.description?.status} testid="identity-description">{desc}</Row>
          ) : null}
          <Row icon={ShieldQuestion} label="Sources found" testid="identity-sources">
            <div className="flex flex-wrap gap-1.5 mt-0.5">
              {sources.map((s, i) => <span key={i} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">{s.name} <ProvBadge status={s.status} /></span>)}
            </div>
          </Row>
        </div>
        {trail.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <button type="button" data-testid="identity-trail-toggle" onClick={() => setShowTrail((v) => !v)}
              className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900">
              <ListChecks className="w-4 h-4" /> How we found this business {showTrail ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showTrail && <div className="mt-3"><TrailList trail={trail} testid="identity-trail" /></div>}
          </div>
        )}
      </div>

      <IdentityEvidence identity={identity} testid="identity-evidence" />

      <OnlinePresence presence={draft.web_discovery} snapshot={draft.snapshot} />

      {addrUnverified && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/70 p-4 flex items-start gap-3" data-testid="identity-address-warning">
          <MapPin className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">{addr ? "This address is AI-suggested, not verified" : "We couldn't confirm a street address"}</p>
            <p className="text-xs text-amber-800/80 mt-0.5">{addr
              ? "No authoritative or public source confirmed this address. Please confirm it is correct or edit it — we never lock an unverified address without your confirmation."
              : "No public source returned a street address for this business. Add it now so your report is accurate — we will never invent one."}</p>
          </div>
          <Button data-testid="identity-add-address" variant="outline" size="sm" onClick={onEdit}
            className="shrink-0 border-amber-300 text-amber-800"><Pencil className="w-3.5 h-3.5 mr-1.5" /> {addr ? "Review" : "Add address"}</Button>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" onClick={onReDiscover} disabled={submitting} data-testid="identity-rediscover"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:border-slate-300 disabled:opacity-50">
          <RefreshCw className="w-4 h-4" /> Re-discover
        </button>
        <button type="button" onClick={onSave} disabled={saving || saved} data-testid="identity-save"
          className={`inline-flex items-center gap-1.5 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${saved ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
          {saved ? <><BookmarkCheck className="w-4 h-4" /> Saved</> : saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Bookmark className="w-4 h-4" /> Save for next time</>}
        </button>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <Button data-testid="identity-yes" disabled={submitting} onClick={onYes}
          className="flex-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold py-6 text-base active:scale-[0.99] transition-transform">
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</> : <><CheckCircle2 className="w-5 h-5 mr-2" /> {mode === "standard" ? "Yes — continue" : "Yes — scan this business"}</>}
        </Button>
        <Button data-testid="identity-edit" variant="outline" onClick={onEdit}
          className="rounded-lg border-slate-300 font-semibold py-6 px-6"><Pencil className="w-4 h-4 mr-2" /> Edit information</Button>
      </div>
      <button type="button" onClick={onRestart} data-testid="identity-restart" className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> No — this isn't my business, start over
      </button>
    </motion.div>
  );
}

/* ---------------- Re-discover: compare current vs newly discovered ---------------- */
function DraftSummary({ d, tone }) {
  const name = d?.name?.value;
  const loc = [d?.location?.city, d?.location?.state].filter(Boolean).join(", ");
  const cat = d?.category?.display_type;
  const web = d?.website || {};
  const ic = d?.identity_confidence || {};
  return (
    <div className={`rounded-xl border p-5 ${tone}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-heading font-semibold text-slate-900 truncate">{name || "—"}</p>
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${CONF[ic.level]?.cls || CONF.LOW.cls}`}>{ic.level || "—"}</span>
      </div>
      <div className="mt-2 space-y-1 text-sm text-slate-600">
        <div><span className="text-slate-400">Location:</span> {loc || "—"}</div>
        <div><span className="text-slate-400">Type:</span> {cat || "Unknown"} <ProvBadge status={d?.category?.status} /></div>
        <div className="break-all"><span className="text-slate-400">Website:</span> {WEB_STATUS[web.status] || web.status} {web.value ? `· ${web.value}` : ""}</div>
      </div>
    </div>
  );
}

function RediscoverCompare({ previous, current, onKeepPrevious, onUseNew }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }} className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="rediscover-compare">
      <p className="text-indigo-600 font-semibold text-xs uppercase tracking-[0.2em] mb-2">Re-discovery</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Compare the results</h1>
      <p className="mt-3 text-slate-600">We ran a fresh discovery. Compare it with what you had and choose which business identity to use for this scan. Your previous evidence is not overwritten.</p>
      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center justify-between mb-2"><p className="text-sm font-semibold text-slate-700">Current</p><Button data-testid="rediscover-keep" variant="outline" size="sm" onClick={onKeepPrevious}>Keep current</Button></div>
          <DraftSummary d={previous} tone="border-slate-200 bg-white" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2"><p className="text-sm font-semibold text-indigo-700">Newly discovered</p><Button data-testid="rediscover-use-new" size="sm" className="bg-slate-900 text-white hover:bg-slate-800" onClick={onUseNew}>Use this</Button></div>
          <DraftSummary d={current} tone="border-indigo-300 bg-indigo-50/40 ring-1 ring-indigo-200" />
        </div>
      </div>
    </motion.div>
  );
}

/* ---------------- Edit / correct information ---------------- */
function EditProfile({ draft, corrections, setCorrections, onDone }) {
  const [f, setF] = useState({
    name: corrections.name ?? draft?.name?.value ?? "",
    city: corrections.city ?? draft?.location?.city ?? "",
    state: corrections.state ?? draft?.location?.state ?? "HI",
    address: corrections.address ?? draft?.location?.address ?? "",
    category: corrections.category ?? "",
    website_url: corrections.website_url ?? draft?.website?.value ?? "",
    description: corrections.description ?? draft?.description?.value ?? "",
  });
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = () => {
    const c = {};
    if (f.name && f.name !== draft?.name?.value) c.name = f.name;
    if (f.city !== (draft?.location?.city || "")) c.city = f.city;
    if (f.state !== (draft?.location?.state || "")) c.state = f.state;
    if (f.address !== (draft?.location?.address || "")) c.address = f.address;
    if (f.category) c.category = f.category;
    if (f.website_url !== (draft?.website?.value || "")) c.website_url = f.website_url;
    if (f.description && f.description !== draft?.description?.value) c.description = f.description;
    setCorrections((x) => ({ ...x, ...c }));
    toast.success("Updated — your changes are recorded as user-confirmed.");
    onDone();
  };
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }} className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="edit-profile">
      <p className="text-indigo-600 font-semibold text-xs uppercase tracking-[0.2em] mb-2">Edit / correct</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Correct your business information</h1>
      <p className="mt-3 text-slate-600">Your edits become user-confirmed values for this scan. We keep the originally discovered values for the record.</p>
      <div className="mt-8 bg-white rounded-xl border border-slate-200 p-6 sm:p-8 shadow-sm space-y-5">
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Business name"><Input data-testid="edit-name" value={f.name} onChange={(e) => upd("name", e.target.value)} /></Field>
          <Field label="Business category">
            <Select value={f.category} onValueChange={(v) => upd("category", v)}>
              <SelectTrigger data-testid="edit-category"><SelectValue placeholder="Keep discovered category" /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <div className="grid sm:grid-cols-3 gap-5">
          <div className="sm:col-span-2"><Field label="City"><Input data-testid="edit-city" value={f.city} onChange={(e) => upd("city", e.target.value)} /></Field></div>
          <Field label="State">
            <Select value={f.state} onValueChange={(v) => upd("state", v)}>
              <SelectTrigger data-testid="edit-state"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Street address" hint="Add or correct the street address. We never invent one."><Input data-testid="edit-address" value={f.address} onChange={(e) => upd("address", e.target.value)} placeholder="123 Main St, Honolulu, HI 96815" /></Field>
        <Field label="Website" hint="Leave blank if there is no website."><Input data-testid="edit-website" value={f.website_url} onChange={(e) => upd("website_url", e.target.value)} placeholder="www.yourbusiness.com" /></Field>
        <Field label="Business description"><Textarea data-testid="edit-description" rows={3} value={f.description} onChange={(e) => upd("description", e.target.value)} /></Field>
      </div>
      <div className="mt-6 flex gap-3">
        <Button data-testid="edit-save" onClick={save} className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6 py-5">Save &amp; review</Button>
        <button type="button" onClick={onDone} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Cancel</button>
      </div>
    </motion.div>
  );
}

/* ---------------- Review & Competitive Position — discover-first (both modes) ---------------- */
const CLASS_CHIP = {
  DIRECT: "text-red-700 bg-red-50 border-red-200",
  NEARBY: "text-sky-700 bg-sky-50 border-sky-200",
  PREMIUM: "text-purple-700 bg-purple-50 border-purple-200",
  VALUE: "text-emerald-700 bg-emerald-50 border-emerald-200",
  UNCATEGORIZED: "text-slate-600 bg-slate-100 border-slate-200",
};
const CLASS_LABEL = {
  DIRECT: "Direct competitor", NEARBY: "Nearby alternative", PREMIUM: "Premium alternative",
  VALUE: "Value alternative", UNCATEGORIZED: "Uncategorized",
};

function ReviewMetricTile({ label, metric, manualValue, onManual, suffix = "" }) {
  const status = metric?.status || "UNABLE_TO_VERIFY";
  const verified = status === "VERIFIED";
  const value = verified && metric?.value != null ? `${metric.value}${suffix}`
    : (manualValue !== "" && manualValue != null ? `${manualValue}${suffix}` : null);
  const badgeStatus = verified ? "VERIFIED" : (manualValue !== "" && manualValue != null ? "USER_PROVIDED" : status);
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid={`rev-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {value != null ? (
            <div className="font-heading text-2xl font-bold text-slate-900 leading-none">{value}</div>
          ) : (
            <div className="text-sm font-semibold text-slate-400">Unable to verify</div>
          )}
          <div className="text-[11px] text-slate-500 mt-1.5">{label}</div>
        </div>
        <ProvBadge status={badgeStatus} />
      </div>
      {!verified && (
        <div className="mt-2">
          <Input type="number" value={manualValue} onChange={(e) => onManual(e.target.value)}
            data-testid={`rev-manual-${label.toLowerCase().replace(/\s+/g, "-")}`}
            placeholder="Enter manually" className="h-8 text-sm" />
        </div>
      )}
      {verified && metric?.checked_at && (
        <div className="text-[10px] text-slate-400 mt-1.5">Checked {String(metric.checked_at).slice(0, 10)}</div>
      )}
    </div>
  );
}

function ReviewCompetitiveStep({ mode, identity, onBack, onConfirm, submitting }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [rev, setRev] = useState({ review_count: "", rating: "", review_response_rate: "" });
  const [comps, setComps] = useState([]);       // {..candidate, selected}
  const [social, setSocial] = useState({ presence: "unsure", accounts: [] });
  const [concerns, setConcerns] = useState({ categories: [], priority_category: null, details: "", goals: "" });
  const [rediscovering, setRediscovering] = useState(false);
  const [showTrail, setShowTrail] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchRes, setSearchRes] = useState(null);  // { candidates, disclaimer, provider_configured } | null

  const initComps = (block) => {
    const cands = block?.candidates || [];
    // Pre-select verified competitors + up to 5 AI leads by relevance.
    const sorted = [...cands].sort((a, b) => (b.relevance || 0) - (a.relevance || 0));
    setComps(cands.map((c) => ({ ...c, selected: c.status === "VERIFIED" || sorted.slice(0, 5).includes(c) })));
  };

  const runDiscover = async (isRe) => {
    if (isRe) setRediscovering(true); else setLoading(true);
    try {
      const { data } = await api.post("/reviews-competitors/discover", identity);
      setData(data);
      initComps(data.competitors);
    } catch (e) {
      toast.error("Discovery hit a snag. You can still enter details manually.");
      setData({ reviews: { metrics: {} }, competitors: { candidates: [], provider_configured: false } });
    } finally { setLoading(false); setRediscovering(false); }
  };

  useEffect(() => { runDiscover(false); /* eslint-disable-next-line */ }, []);

  const reviews = data?.reviews || {};
  const metrics = reviews.metrics || {};
  const compBlock = data?.competitors || {};
  const providerOn = !!data?.provider_configured;
  const selectedCount = comps.filter((c) => c.selected).length;

  const toggle = (i) => setComps((l) => l.map((c, j) => (j === i ? { ...c, selected: !c.selected } : c)));
  const remove = (i) => setComps((l) => l.filter((_, j) => j !== i));

  const runSearch = async () => {
    const q = searchQ.trim();
    if (!q || searching) return;
    setSearching(true);
    try {
      const { data } = await api.post("/reviews-competitors/search-competitor", {
        query: q, city: identity.city, state: identity.state, category: identity.category,
      });
      setSearchRes(data);
    } catch (e) {
      toast.error("Search hit a snag. Please try again.");
      setSearchRes({ candidates: [], disclaimer: "", provider_configured: false });
    } finally { setSearching(false); }
  };

  const addFromSearch = (cand) => {
    // De-dupe by place_id (verified) or lowercased name.
    const key = (cand.place_id || cand.name || "").toString().toLowerCase();
    const exists = comps.some((c) => (c.place_id || c.name || "").toString().toLowerCase() === key);
    if (exists) { toast.info("That competitor is already in your set."); return; }
    setComps((l) => [...l, { ...cand, selected: true }]);
    setSearchQ("");
    setSearchRes(null);
    setAddOpen(false);
    toast.success(`Added ${cand.name}.`);
  };

  const confirm = () => {
    const manualData = (rev.review_count || rev.rating || rev.review_response_rate) ? {
      review_count: rev.review_count ? Number(rev.review_count) : null,
      rating: rev.rating ? Number(rev.rating) : null,
      review_response_rate: rev.review_response_rate ? Number(rev.review_response_rate) : null,
    } : null;
    const confirmedComps = comps.filter((c) => c.selected && (c.name || "").trim()).map((c) => ({
      name: c.name,
      review_count: c.review_count != null ? Number(c.review_count) : null,
      rating: c.rating != null ? Number(c.rating) : null,
      website_url: c.website || null, phone: c.phone || null, category: c.category || null,
      address: c.address || null,
      place_id: c.place_id || null, classification: c.classification || "UNCATEGORIZED",
      source: c.source || "AI discovery", status: c.status || "AI_SUGGESTED",
      relevance: typeof c.relevance === "number" ? c.relevance : null,
      why: c.why || null, checked_at: c.checked_at || null,
    }));
    onConfirm({ manual: manualData, competitors: confirmedComps,
                social_presence: social.presence, social_accounts: social.accounts,
                owner_concerns: concerns });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }} className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="revcomp-step">
      <p className="text-indigo-600 font-semibold text-xs uppercase tracking-[0.2em] mb-2">Research · {mode === "bi" ? "360° scan" : "Detailed scan"}</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Review &amp; Competitive Position</h1>
      <p className="mt-3 text-slate-600">We research this automatically — you just confirm. We never invent numbers; anything we can't verify is shown as “Unable to verify”, with a manual option.</p>

      {/* Your review data */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="revcomp-reviews">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" /><h2 className="font-heading font-semibold text-slate-900">Your review data</h2></div>
          {reviews.source && <ProvBadge status={reviews.overall_status === "VERIFIED" ? "VERIFIED" : "UNABLE_TO_VERIFY"} />}
        </div>
        {loading ? (
          <div className="py-6 grid place-items-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mb-1.5" /> Researching public sources…</div>
        ) : (
          <div className="grid sm:grid-cols-3 gap-4">
            <ReviewMetricTile label="Total reviews" metric={metrics.review_count} manualValue={rev.review_count} onManual={(v) => setRev((r) => ({ ...r, review_count: v }))} />
            <ReviewMetricTile label="Average rating" metric={metrics.rating} manualValue={rev.rating} onManual={(v) => setRev((r) => ({ ...r, rating: v }))} suffix=" / 5" />
            <ReviewMetricTile label="Response rate" metric={metrics.review_response_rate} manualValue={rev.review_response_rate} onManual={(v) => setRev((r) => ({ ...r, review_response_rate: v }))} suffix="%" />
          </div>
        )}
        {!loading && !providerOn && (
          <p className="mt-3 text-[11px] text-slate-400">No verified review provider is connected, so these can't be independently verified yet. Enter them to include a self-reported (You provided) reputation score, or connect Google Places later for VERIFIED data.</p>
        )}
      </div>

      {/* Competitive set */}
      <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="revcomp-competitors">
        <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
          <div className="flex items-center gap-2"><Users className="w-5 h-5 text-slate-700" /><h2 className="font-heading font-semibold text-slate-900">Competitive set</h2></div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => { setAddOpen((v) => !v); setSearchRes(null); }} data-testid="revcomp-add"
              className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"><Plus className="w-4 h-4" /> Add</button>
            <button type="button" onClick={() => runDiscover(true)} disabled={rediscovering} data-testid="revcomp-rediscover"
              className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50">
              {rediscovering ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Re-discover
            </button>
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          {loading ? "Finding businesses that compete for the same customers…"
            : comps.length ? `We found ${comps.length} potential competitor${comps.length !== 1 ? "s" : ""}. Select the ones that truly compete with you.`
            : "We couldn't confidently identify competitors from available sources. Use “Add” to search for one by name, address, website or phone."}
        </p>

        {addOpen && (
          <div className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4" data-testid="revcomp-add-panel">
            <label className="text-xs font-semibold text-slate-700">Search for a competitor</label>
            <p className="text-[11px] text-slate-500 mb-2">By business name, address, website or phone — we find it and fill in verified ratings &amp; reviews when available, so you never have to know the numbers.</p>
            <div className="flex gap-2">
              <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(); } }}
                placeholder="e.g. Maguro Spot, 123 Main St, or a phone number" data-testid="revcomp-search-input" />
              <Button type="button" onClick={runSearch} disabled={searching || !searchQ.trim()} data-testid="revcomp-search-btn"
                className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white shrink-0">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                <span className="ml-1.5 hidden sm:inline">Search</span>
              </Button>
            </div>

            {searchRes && (
              <div className="mt-3" data-testid="revcomp-search-results">
                {searchRes.candidates?.length ? (
                  <div className="space-y-2">
                    {searchRes.candidates.map((cand, i) => (
                      <div key={i} data-testid={`revcomp-search-result-${i}`}
                        className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-800 truncate">{cand.name}</span>
                            <ProvBadge status={cand.status} />
                          </div>
                          {cand.address && <p className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{cand.address}</p>}
                          <div className="text-xs text-slate-500 mt-0.5">
                            {(cand.rating != null || cand.review_count != null)
                              ? <span className="inline-flex items-center gap-2">{cand.rating != null && <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-500" />{cand.rating}</span>}{cand.review_count != null && <span>{cand.review_count} reviews</span>}<span className="text-slate-400">· {cand.source}</span></span>
                              : <span className="text-slate-400">Ratings &amp; reviews: Unable to verify · {cand.source}</span>}
                          </div>
                          {cand.verify_url && <a href={cand.verify_url} target="_blank" rel="noreferrer" className="text-[11px] text-indigo-600 hover:underline inline-flex items-center gap-0.5 mt-1">Check on Google Maps <ExternalLink className="w-3 h-3" /></a>}
                        </div>
                        <Button type="button" onClick={() => addFromSearch(cand)} data-testid={`revcomp-search-add-${i}`}
                          className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shrink-0">
                          <Plus className="w-3.5 h-3.5 mr-1" /> Confirm &amp; add
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 py-2" data-testid="revcomp-search-empty">No matches found. Try a different name, address, website or phone.</p>
                )}
                {searchRes.disclaimer && <p className="mt-2 text-[11px] text-slate-400 leading-relaxed">{searchRes.disclaimer}</p>}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="py-6 grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="space-y-2" data-testid="revcomp-list">
            {comps.map((c, i) => (
              <label key={i} data-testid={`revcomp-item-${i}`}
                className={`flex items-start gap-3 rounded-xl border p-3.5 cursor-pointer transition-colors ${c.selected ? "border-indigo-300 bg-indigo-50/40" : "border-slate-200 hover:border-slate-300"}`}>
                <input type="checkbox" checked={!!c.selected} onChange={() => toggle(i)} data-testid={`revcomp-check-${i}`}
                  className="mt-1 w-4 h-4 accent-indigo-600" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-800 truncate">{c.name}</span>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${CLASS_CHIP[c.classification] || CLASS_CHIP.UNCATEGORIZED}`}>{CLASS_LABEL[c.classification] || "Uncategorized"}</span>
                    <ProvBadge status={c.status || "AI_SUGGESTED"} />
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {c.rating != null || c.review_count != null
                      ? <span className="inline-flex items-center gap-2">{c.rating != null && <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-500" />{c.rating}</span>}{c.review_count != null && <span>{c.review_count} reviews</span>}</span>
                      : <span className="text-slate-400">Ratings &amp; review counts unable to verify</span>}
                    {c.why && <span className="text-slate-400"> · {c.why}</span>}
                  </div>
                </div>
                <button type="button" onClick={(e) => { e.preventDefault(); remove(i); }} data-testid={`revcomp-remove-${i}`} className="text-slate-300 hover:text-red-500 shrink-0"><X className="w-4 h-4" /></button>
              </label>
            ))}
            {!comps.length && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 flex gap-3">
                <Info className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                <p className="text-sm text-slate-600">No competitors were confidently identified. Add them manually, or run the scan — the 360° scan will still research your market.</p>
              </div>
            )}
          </div>
        )}

        {/* How we found this */}
        {!loading && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <button type="button" onClick={() => setShowTrail((v) => !v)} data-testid="revcomp-trail-toggle"
              className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900">
              <ListChecks className="w-4 h-4" /> How we found this {showTrail ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showTrail && (
              <div className="mt-3 space-y-1.5 text-sm" data-testid="revcomp-trail">
                <TrailRow ok={providerOn} label="Review data" detail={providerOn ? `From ${reviews.source}` : "No verified provider connected — enter manually or connect Google Places"} status={providerOn ? "PUBLIC_SOURCE" : "NOT_CONFIGURED"} />
                <TrailRow ok={compBlock.provider_configured} label="Competitor data" detail={compBlock.provider_configured ? "Verified public business profiles" : "AI-suggested by category, market & relevance (not verified)"} status={compBlock.provider_configured ? "PUBLIC_SOURCE" : "AI_SUGGESTED"} />
                <TrailRow ok label="Business identity" detail={`${identity.name}${identity.city ? ` · ${identity.city}, ${identity.state}` : ""} · ${identity.category || "Business"}`} status="USER_CONFIRMED" />
              </div>
            )}
          </div>
        )}
        {compBlock.maps_search_url && (
          <a href={compBlock.maps_search_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700"><MapPin className="w-4 h-4" /> Find more competitors on Google Maps</a>
        )}
      </div>

      <div className="mt-6">
        <SocialAccountsInput onChange={setSocial} />
      </div>

      <div className="mt-6">
        <OwnerConcernsInput onChange={setConcerns} />
      </div>

      <div className="mt-6 flex gap-3">
        <Button data-testid="revcomp-confirm" onClick={confirm} disabled={submitting}
          className="flex-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold py-6 text-base">
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting scan…</> : <>Confirm &amp; Run Scan <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 px-2"><ArrowLeft className="w-4 h-4" /> Back</button>
      </div>
      <p className="mt-3 text-center text-xs text-slate-400">{selectedCount} competitor{selectedCount !== 1 ? "s" : ""} selected · You can also skip and let the scan research your market.</p>
    </motion.div>
  );
}

function TrailRow({ ok, label, detail, status }) {
  return (
    <div className="flex items-start gap-2">
      <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${ok ? "text-emerald-500" : "text-slate-300"}`} />
      <div className="min-w-0 flex-1"><span className="text-slate-700 font-medium">{label}</span>{detail && <span className="text-slate-400"> — {detail}</span>}</div>
      <ProvBadge status={status} />
    </div>
  );
}

/* ---------------- Cancelled ---------------- */
function CancelledScreen({ onRestart }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="max-w-xl mx-auto px-5 sm:px-8 py-16 text-center" data-testid="scan-cancelled">
      <div className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-slate-100 mb-6">
        <Ban className="w-7 h-7 text-slate-500" />
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Scan cancelled</h1>
      <p className="mt-3 text-slate-500">Your scan was stopped. No report was generated. You can start a fresh scan whenever you're ready.</p>
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button onClick={onRestart} data-testid="scan-cancelled-restart" className="bg-slate-900 hover:bg-slate-800">
          <ScanSearch className="w-4 h-4 mr-2" /> Start a new scan
        </Button>
        <Link to="/dashboard" data-testid="scan-cancelled-dashboard"
          className="text-sm text-slate-500 hover:text-indigo-600 underline underline-offset-2">Back to dashboard</Link>
      </div>
    </motion.div>
  );
}

/* ---------------- Scanning ---------------- */
function ScanningScreen({ scanId, biz, onDone, onFail, onCancel }) {
  const [percent, setPercent] = useState(0);
  const [label, setLabel] = useState("Initializing scan…");
  const [logs, setLogs] = useState([]);
  const [cancelling, setCancelling] = useState(false);
  const doneStages = useRef(new Set());

  useEffect(() => {
    if (!scanId) return;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/scan/${scanId}/status`);
        if (!active) return;
        const p = data.progress?.percent ?? 0;
        setPercent(p);
        if (data.progress?.label) setLabel(data.progress.label);
        SCAN_STAGES.forEach((s, i) => {
          if (p >= THRESH[i] && !doneStages.current.has(s.key)) {
            doneStages.current.add(s.key);
            setLogs((l) => [...l, `✓ ${s.label} analyzed`]);
          }
        });
        if (data.status === "complete") { setPercent(100); setTimeout(onDone, 700); return; }
        if (data.status === "failed") { onFail(); return; }
        if (data.status === "cancelled") { return; }
        setTimeout(poll, 1300);
      } catch (e) { setTimeout(poll, 1600); }
    };
    poll();
    return () => { active = false; };
  }, [scanId]);

  const cancel = async () => {
    setCancelling(true);
    try { await api.post(`/scan/${scanId}/cancel`); } catch (e) { /* ignore */ }
    onCancel?.();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
      <div className="text-center mb-10">
        <p className="text-indigo-600 font-semibold text-xs uppercase tracking-[0.2em] mb-2">Scanning · Business confirmed</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Analyzing your business…</h1>
        <p className="mt-2 text-slate-500">{biz.name} · {[biz.city, biz.state].filter(Boolean).join(", ")}</p>
      </div>
      <div className="relative grid place-items-center h-56 mb-8">
        <div className="absolute w-52 h-52 rounded-full border border-slate-200" />
        <div className="absolute w-36 h-36 rounded-full border border-slate-200" />
        <div className="absolute w-20 h-20 rounded-full border border-slate-200" />
        <div className="absolute w-52 h-52 rounded-full animate-ping-slow bg-indigo-500/10" />
        <div className="absolute w-52 h-52 rounded-full overflow-hidden animate-radar"
          style={{ background: "conic-gradient(from 0deg, rgba(79,70,229,0) 0deg, rgba(79,70,229,0.35) 60deg, rgba(79,70,229,0) 90deg)" }} />
        <div className="relative grid place-items-center w-16 h-16 rounded-full bg-slate-900 text-white shadow-lg"><ScanSearch className="w-7 h-7" /></div>
      </div>
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2 text-sm">
          <span className="font-medium text-slate-700 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-indigo-600" /> {label}</span>
          <span className="font-semibold text-slate-900 font-mono">{percent}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden">
          <motion.div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-indigo-600" animate={{ width: `${percent}%` }} transition={{ ease: "easeOut", duration: 0.6 }} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mb-8">
        {SCAN_STAGES.map((s, i) => {
          const done = percent >= THRESH[i];
          const active = !done && (i === 0 || percent >= THRESH[i - 1]);
          return (
            <div key={s.key} className={`flex items-start gap-3 rounded-xl border p-4 transition-colors ${done ? "border-emerald-200 bg-emerald-50/50" : active ? "border-indigo-200 bg-indigo-50/40" : "border-slate-200 bg-white"}`}>
              <span className="mt-0.5 shrink-0">{done ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : active ? <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" /> : <span className="block w-5 h-5 rounded-full border-2 border-slate-200" />}</span>
              <div><p className={`text-sm font-semibold ${done ? "text-emerald-700" : "text-slate-800"}`}>{s.label}</p><p className="text-xs text-slate-500">{s.desc}</p></div>
            </div>
          );
        })}
      </div>
      <div className="rounded-xl bg-slate-900 p-4 font-mono text-xs text-slate-300 h-32 overflow-hidden">
        <div className="text-slate-500 mb-1">$ {biz.website_url ? `scanning ${biz.website_url}` : "analyzing online presence (no website)"}</div>
        {logs.map((l, i) => <div key={i} className="text-emerald-400">{l}</div>)}
        <div className="text-slate-400 animate-pulse">▍ working…</div>
      </div>
      <div className="mt-5 flex flex-col items-center gap-2">
        <p className="text-xs text-slate-400 text-center">This scan runs on our servers — you can safely close or refresh this tab and come back; it will keep going.</p>
        <button type="button" onClick={cancel} disabled={cancelling} data-testid="scan-cancel-btn"
          className="text-sm text-slate-500 hover:text-red-600 underline underline-offset-2 disabled:opacity-50">
          {cancelling ? "Cancelling…" : "Cancel scan"}
        </button>
      </div>
    </motion.div>
  );
}
